import { Header, renderServiceTabs, AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../../components';
import { setState, navigateTo } from '../../store';
import { getOutletsByType, getMiceConfig } from '../../api';
import { getCurrentLanguage, formatLocalDate, t } from '../../i18n';
import { term } from '../../miniapp-config';
import type { MiniAppService } from '@revio/shared';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

interface LocalizedString { th: string; en: string; zh: string; ru?: string; ja?: string; ko?: string; }

interface RfpOutlet {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  images?: Array<{ url: string; caption?: string }>;
}

let rfpOutlets: RfpOutlet[] = [];
let activeRfpOutletId: string | null = null;
let rfpLoaded = false;

let eventDateStr = formatLocalDate();
let expectedAttendees = 20;
let rfpRequirementText = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.en || val.th || val.zh || val.ru || val.ja || val.ko || fallback;
  }
  return fallback;
}

export function renderRequestRfpEngine(service: MiniAppService): string {
  const lang = getCurrentLanguage();
  const serviceId = service?.id || 'inquiry';
  const svcTitle = getLocalizedText(service.label, lang, t('appName'));

  const itemTerm = term('item', serviceId, t('termItem'));
  const locationTerm = term('location', serviceId, t('termLocation'));

  const activeOutlet = rfpOutlets.find(o => o.id === activeRfpOutletId) || rfpOutlets[0] || {};
  const outletName = getLocalizedText(activeOutlet.name, lang, svcTitle);

  return `
    ${Header(outletName, false, true)}
    ${renderServiceTabs('service', serviceId)}

    <div class="page-container" id="rfp-content" style="padding:16px;padding-bottom:100px;">
      ${rfpOutlets.length > 1 ? `
        <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;">
          ${rfpOutlets.map((o) => `
            <button 
              type="button"
              onclick="window.switchRfpOutlet?.('${o.id}')"
              style="flex-shrink:0;padding:8px 14px;border-radius:20px;font-size:13px;font-weight:600;border:${o.id === activeRfpOutletId ? '1.5px solid #2563eb' : '1px solid #e0e0e0'};background:${o.id === activeRfpOutletId ? '#eff6ff' : '#fff'};color:${o.id === activeRfpOutletId ? '#1d4ed8' : '#555'};cursor:pointer;white-space:nowrap;"
            >
              📍 ${getLocalizedText(o.name, lang, locationTerm)}
            </button>
          `).join('')}
        </div>
      ` : ''}

      <!-- Date Selection Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="font-size:14px;font-weight:700;color:#111;margin-bottom:12px;">📅 ${lang === 'th' ? 'วันที่ต้องการรับบริการ / นัดหมาย' : 'Requested Date'}</div>
        <div 
          id="rfp-date-trigger" 
          style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border:1px solid #e0e0e0;border-radius:8px;background:#f9f9f9;cursor:pointer;"
        >
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:16px;">🗓️</span>
            <span id="rfp-date-display" style="font-size:13px;font-weight:600;color:#333;">${eventDateStr}</span>
          </div>
          <span id="rfp-date-toggle-icon" style="font-size:12px;color:#888;transition:transform 0.2s;">▼</span>
        </div>
        <div id="rfp-picker-container" class="litepicker-inline collapsed" style="margin-top:12px;"></div>
      </div>

      <!-- Attendees or Scale -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:14px;font-weight:700;color:#111;">👥 ${lang === 'th' ? 'จำนวนผู้เข้าร่วม / ขนาดงาน' : 'Quantity / Scale'}</div>
            <div style="font-size:11px;color:#888;">${t('guests') || 'Pax / Units'}</div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;background:#f5f5f5;border-radius:20px;padding:4px 10px;">
            <button onclick="window.changeRfpAttendees?.(-5)" style="border:none;background:none;font-size:18px;font-weight:700;color:#2563eb;cursor:pointer;">-</button>
            <span id="rfp-attendees-display" style="font-size:14px;font-weight:600;min-width:28px;text-align:center;">${expectedAttendees}</span>
            <button onclick="window.changeRfpAttendees?.(5)" style="border:none;background:none;font-size:18px;font-weight:700;color:#2563eb;cursor:pointer;">+</button>
          </div>
        </div>
      </div>

      <!-- Details & Requirements -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <label style="display:block;font-size:14px;font-weight:700;color:#111;margin-bottom:8px;">
          📝 ${lang === 'th' ? 'รายละเอียดความต้องการ / โครงการ' : 'Requirements / Scope'}
        </label>
        <textarea 
          id="rfp-requirements-input"
          rows="3"
          onchange="window.setRfpRequirements?.(this.value)"
          placeholder="${lang === 'th' ? 'ระบุรายละเอียดที่ต้องการขอใบเสนอราคา หรือข้อกำหนดเฉพาะ' : 'Describe your requirements or questions...'}"
          style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:13px;outline:none;resize:vertical;"
        >${rfpRequirementText}</textarea>
      </div>

      <!-- Notice Banner -->
      <div style="background:#eff6ff;border-left:4px solid #3b82f6;border-radius:8px;padding:12px;margin-bottom:20px;">
        <div style="font-size:13px;font-weight:600;color:#1d4ed8;margin-bottom:4px;">
          💬 ${t('quotationPending')}
        </div>
        <div style="font-size:12px;color:#3b82f6;line-height:1.4;">
          ${lang === 'th' ? 'เจ้าหน้าที่จะติดต่อประสานงานและจัดทำใบเสนอราคาให้คุณโดยเร็วที่สุด' : 'Our team will contact you shortly with a personalized quotation.'}
        </div>
      </div>

      <button 
        type="button" 
        class="btn btn-primary btn-block btn-lg" 
        onclick="window.proceedRfpToGuestInfo?.()"
      >
        ${t('next')} ›
      </button>

      ${AppVersionFooter()}
    </div>

    ${BackToTopButton('rfp')}
    ${FloatingNav()}
  `;
}

export function initRequestRfpEngine(service: MiniAppService): void {
  const serviceId = service?.id || 'inquiry';
  const outletType = service?.outletType || 'venue';

  if (!rfpLoaded) {
    getOutletsByType(outletType).then((outlets) => {
      rfpLoaded = true;
      if (Array.isArray(outlets) && outlets.length > 0) {
        rfpOutlets = outlets;
        activeRfpOutletId = outlets[0].id || null;
        (window as any).renderApp?.();
      }
    }).catch(() => {
      rfpLoaded = true;
    });
  }

  (window as any).switchRfpOutlet = (id: string) => {
    activeRfpOutletId = id;
    (window as any).renderApp?.();
  };

  (window as any).changeRfpAttendees = (delta: number) => {
    expectedAttendees = Math.max(1, expectedAttendees + delta);
    const disp = document.getElementById('rfp-attendees-display');
    if (disp) disp.textContent = String(expectedAttendees);
  };

  (window as any).setRfpRequirements = (val: string) => {
    rfpRequirementText = val;
  };

  (window as any).proceedRfpToGuestInfo = () => {
    const lang = getCurrentLanguage();
    const activeOutlet = rfpOutlets.find(o => o.id === activeRfpOutletId) || rfpOutlets[0] || {};
    const txtArea = document.getElementById('rfp-requirements-input') as HTMLTextAreaElement | null;
    if (txtArea) rfpRequirementText = txtArea.value;

    const outletName = getLocalizedText(activeOutlet.name, lang, getLocalizedText(service.label, lang, 'RFP'));

    setState({
      bookingType: 'RFP',
      engine: 'request_rfp',
      activeServiceId: serviceId,
      resourceId: activeOutlet.id || undefined,
      resourceName: outletName,
      miceDetails: {
        functionRoomId: activeOutlet.id || 'rfp-resource',
        functionRoomName: outletName,
        eventType: 'Inquiry / RFP',
        layoutStyle: 'standard',
        eventDate: eventDateStr,
        expectedPax: expectedAttendees,
        specialRequests: rfpRequirementText,
        isRfpInquiry: true,
      },
      selectedRoom: {
        roomTypeCode: activeOutlet.id || 'RFP',
        roomTypeName: outletName,
        ratePlanCode: 'RFP',
        ratePlanName: lang === 'th' ? 'ขอใบเสนอราคา (Quotation on Request)' : 'Quotation on Request',
        totalRate: 0,
        ratePerRoom: 0,
        averageNightlyRate: 0,
        totalBeforeTax: 0,
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
        description: `${eventDateStr} (${expectedAttendees} pax)`,
      },
    });

    navigateTo('guest-info');
  };

  // Setup Litepicker
  const trigger = document.getElementById('rfp-date-trigger');
  const container = document.getElementById('rfp-picker-container');
  const toggleIcon = document.getElementById('rfp-date-toggle-icon');
  if (trigger && container) {
    trigger.addEventListener('click', () => {
      container.classList.toggle('collapsed');
      if (toggleIcon) {
        toggleIcon.style.transform = container.classList.contains('collapsed') ? '' : 'rotate(180deg)';
      }
    });

    new Litepicker({
      element: container,
      inlineMode: true,
      singleMode: true,
      minDate: new Date(),
      startDate: eventDateStr,
      format: 'YYYY-MM-DD',
      setup: (picker) => {
        picker.on('selected', (date) => {
          if (date) {
            eventDateStr = date.format('YYYY-MM-DD');
            const disp = document.getElementById('rfp-date-display');
            if (disp) disp.textContent = eventDateStr;
            container.classList.add('collapsed');
            if (toggleIcon) toggleIcon.style.transform = '';
          }
        });
      },
    });
  }

  window.handleBack = () => {
    navigateTo('search');
  };

  initBackToTop('rfp');
}
